import type {
  ComposeOutputMode,
  ExitHookReadiness,
  InjectReadiness,
  PineEntrySite,
  PineGuardContext,
  PineGuardContextUx,
  PineIr,
  PineParseIssue,
  PineParseIssueSeverity,
  PineParseStatus,
  PineParseWarning,
  PineWarningCatalogEntry,
  ReportStrategyPineFields,
  ReportStrategyPineParseResult,
  ReportStrategyPineRequirementsResponse,
} from "../types/reportStrategyPine";

export type ComposeModeHelpEntry = {
  id: ComposeOutputMode;
  label: string;
  description: string;
};

const COMPOSE_MODE_HELP_FALLBACK: ComposeModeHelpEntry[] = [
  {
    id: "merge_into_strategy",
    label: "Merge into my strategy",
    description:
      "One strategy script on TradingView. Tessera adds a filter block and gates when your script opens trades. Your entry logic stays exactly as you wrote it.",
  },
  {
    id: "append_policy_to_indicator",
    label: "Append policy to indicator",
    description:
      "One indicator script. Your plots and alerts stay as-is. Tessera removes any old policy block and appends a new // @tessera:begin policy … // @tessera:end section at the bottom, with chart labels and tessera_allowed_* variables.",
  },
  {
    id: "separate_indicator",
    label: "Separate indicator",
    description:
      "Two scripts: your strategy stays unchanged, plus a Tessera indicator that shows on-chart labels for which rule blocked a trade or what context is missing.",
  },
];

const COMPOSE_MODE_DISPLAY_ORDER: ComposeOutputMode[] = [
  "merge_into_strategy",
  "append_policy_to_indicator",
  "separate_indicator",
];

export type ParseStatusHelpEntry = {
  id: PineParseStatus;
  label: string;
  description: string;
};

export type PineEnumUx = {
  /** Compact label for badges and chips */
  shortLabel: string;
  /** Heading in banners and summaries */
  title: string;
  /** Full sentence(s) for tooltips and alert body */
  description: string;
};

const PARSE_STATUS_UX_CANONICAL: Record<PineParseStatus, PineEnumUx> = {
  full: {
    shortLabel: "Parsed successfully",
    title: "Parse successful",
    description: "Pinescript parsed successfully.",
  },
  partial: {
    shortLabel: "Partial",
    title: "Parse partial",
    description:
      "Script parsed with warnings. Review issues below; merge compose depends on inject readiness.",
  },
  failed: {
    shortLabel: "Failed",
    title: "Parse failed",
    description:
      "Parse failed. Fix errors below. Merge is blocked; Tessera uses your written description in reports until Pinescript parses successfully.",
  },
};

const INJECT_READINESS_UX_CANONICAL: Record<InjectReadiness, PineEnumUx> = {
  ready: {
    shortLabel: "Ready for merge",
    title: "Inject ready",
    description:
      "All detected trade entry points can receive policy gates. Merge into strategy is available when parse status allows.",
  },
  needs_review: {
    shortLabel: "Needs review",
    title: "Inject needs review",
    description:
      "Some entries are not auto-mergeable. Review the entry table. You can still compose with Separate indicator or Append policy to indicator.",
  },
  unsupported: {
    shortLabel: "Unsupported",
    title: "Inject unsupported",
    description:
      "Policy cannot merge into entries in this script. Use Separate indicator or fix entry structure, then re-parse.",
  },
  not_applicable: {
    shortLabel: "Entry merge off",
    title: "Entry merge not available",
    description:
      "This script type does not use merge-into-strategy. Choose Append policy to indicator or Separate indicator when composing.",
  },
};

const EXIT_HOOK_READINESS_UX_CANONICAL: Record<ExitHookReadiness, PineEnumUx> = {
  ready: {
    shortLabel: "Exit hooks ready",
    title: "Exit hooks ready",
    description:
      "Named stop/limit variables detected. Compose can patch your exit orders when policy requires it.",
  },
  partial: {
    shortLabel: "Exit hooks partial",
    title: "Exit hooks partial",
    description:
      "Some exits are hookable; others will get labels only. Use named stop/limit variables or // @tessera:exit_vars for best results.",
  },
  unsupported: {
    shortLabel: "Exit hooks unsupported",
    title: "Exit hooks unsupported",
    description:
      "Compose cannot patch exit orders. Management may appear as labels and comments only on TradingView.",
  },
  not_applicable: {
    shortLabel: "Exit hooks n/a",
    title: "Exit hooks not applicable",
    description: "No strategy.exit hooks expected for this script type.",
  },
};

const PINE_SCRIPT_KIND_UX_CANONICAL: Record<string, PineEnumUx> = {
  strategy: {
    shortLabel: "Strategy script",
    title: "Strategy script",
    description:
      "Strategy script. Policy can merge into your trade entries when parse and inject readiness allow.",
  },
  indicator: {
    shortLabel: "Indicator script",
    title: "Indicator script",
    description:
      "Indicator script. Strategy entry merge does not apply. Use Append policy to indicator or Separate indicator when composing.",
  },
};

const PARSE_STATUS_HELP: ParseStatusHelpEntry[] = (
  ["full", "partial", "failed"] as PineParseStatus[]
).map((id) => ({
  id,
  label: PARSE_STATUS_UX_CANONICAL[id].shortLabel,
  description: PARSE_STATUS_UX_CANONICAL[id].description,
}));

const PARSE_STATUS_DISPLAY_ORDER: PineParseStatus[] = ["full", "partial", "failed"];

/** Canonical help copy for the requirements panel (not the terse BE parse_status_ux strings). */
export function getParseStatusHelp(): ParseStatusHelpEntry[] {
  return PARSE_STATUS_DISPLAY_ORDER.map(
    (id) => PARSE_STATUS_HELP.find((e) => e.id === id) ?? { id, label: id, description: "" }
  );
}

export type ParseStageCapabilityCell = "yes" | "no" | "conditional";

export type ParseStageCapabilityRow = {
  id: string;
  label: string;
  hint?: string;
  full: ParseStageCapabilityCell;
  partial: ParseStageCapabilityCell;
  failed: ParseStageCapabilityCell;
  /** Shown on the Partial column when value is conditional. */
  partialNote?: string;
};

const PARSE_STAGE_CAPABILITY_MATRIX: ParseStageCapabilityRow[] = [
  {
    id: "save_pine",
    label: "Save strategy with Pinescript",
    hint: "You must click Parse before save when the editor has script text. Any parse result is allowed.",
    full: "yes",
    partial: "yes",
    failed: "yes",
  },
  {
    id: "strategy_in_report",
    label: "Strategy passed into reports",
    hint: "When you generate a report with this strategy linked, its Pinescript (or description if parse failed) is included in the report input.",
    full: "yes",
    partial: "yes",
    failed: "no",
  },
  {
    id: "entry_table",
    label: "Entry conditions table",
    hint: "Shown after Parse from pine_ir.entry_sites.",
    full: "yes",
    partial: "yes",
    failed: "no",
  },
  {
    id: "merge_into_strategy",
    label: "Merge policy into strategy",
    hint: "Requires strategy() script with injectable entries, not indicator-only scripts.",
    full: "yes",
    partial: "conditional",
    failed: "no",
    partialNote: "Inject ready",
  },
  {
    id: "indicator_compose",
    label: "Append / separate indicator export",
    hint: "TradingView compose for indicator scripts or separate overlay mode.",
    full: "yes",
    partial: "yes",
    failed: "no",
  },
  {
    id: "combine_policies",
    label: "Combine policy versions",
    hint: "Merge saved policy versions into one bundle (independent of last parse status).",
    full: "yes",
    partial: "yes",
    failed: "yes",
  },
];

export function getParseStageCapabilityMatrix(): ParseStageCapabilityRow[] {
  return PARSE_STAGE_CAPABILITY_MATRIX;
}

/**
 * Help-panel compose modes: always use FE copy for descriptions.
 * API compose_modes may supply labels; BE also drives export gating elsewhere.
 */
export function mergeComposeModesForHelp(
  apiModes?: ReportStrategyPineRequirementsResponse["compose_modes"]
): ComposeModeHelpEntry[] {
  const byId = new Map<ComposeOutputMode, ComposeModeHelpEntry>();
  for (const entry of COMPOSE_MODE_HELP_FALLBACK) {
    byId.set(entry.id, { ...entry });
  }
  for (const m of apiModes ?? []) {
    const id = m.id;
    if (
      id !== "merge_into_strategy" &&
      id !== "separate_indicator" &&
      id !== "append_policy_to_indicator"
    ) {
      continue;
    }
    const base = byId.get(id)!;
    byId.set(id, {
      id,
      label: coerceUxCopy(m.label) ?? base.label,
      description: coerceUxCopy(m.description) ?? base.description,
    });
  }
  return COMPOSE_MODE_DISPLAY_ORDER.map((id) => byId.get(id)).filter((x): x is ComposeModeHelpEntry => Boolean(x));
}

/** Label + description for one compose mode (FE fallback, optional API overrides). */
export function getComposeModeHelpEntry(
  mode?: ComposeOutputMode | string | null,
  apiModes?: ReportStrategyPineRequirementsResponse["compose_modes"]
): ComposeModeHelpEntry | null {
  if (!mode) return null;
  return mergeComposeModesForHelp(apiModes).find((e) => e.id === mode) ?? null;
}

export type GuardContextMeta = { label: string; tooltip: string };

const GUARD_CONTEXT_META: Record<string, GuardContextMeta> = {
  direct_if: {
    label: "if",
    tooltip: "Entry directly under an if",
  },
  if_block: {
    label: "if (block)",
    tooltip: "Assignments between if and entry (normal)",
  },
  else_if: {
    label: "else if",
    tooltip: "Synthetic guard may include (not …) and …",
  },
  else_branch: {
    label: "else",
    tooltip: "Synthetic (not parent_if)",
  },
  switch_arm: {
    label: "switch",
    tooltip: "condition => strategy.entry",
  },
  when_arg: {
    label: "when=",
    tooltip: "strategy.entry(..., when=…)",
  },
  inline_if: {
    label: "inline if",
    tooltip: "strategy.entry(...) if cond",
  },
  tessera_marker: {
    label: "manual",
    tooltip: "// @tessera:inject long",
  },
  function_body: {
    label: "function",
    tooltip: "Not auto-mergeable",
  },
  unsupported_enclosing: {
    label: "loop / unsupported",
    tooltip: "Use @tessera:inject",
  },
  no_enclosing_if: {
    label: "no guard",
    tooltip: "Fix script or add marker",
  },
};

export function getRequirementsWarningCatalog(
  requirements?: ReportStrategyPineRequirementsResponse | null
): Record<string, PineWarningCatalogEntry> | undefined {
  if (requirements?.warning_catalog) return requirements.warning_catalog;
  const legacy = requirements?.warning_codes;
  if (!legacy) return undefined;
  const out: Record<string, PineWarningCatalogEntry> = {};
  for (const [code, entry] of Object.entries(legacy)) {
    out[code] = { title: entry.title, message: entry.message };
  }
  return out;
}

export function guardContextMeta(
  context?: PineGuardContext | string | null,
  uxCatalog?: Record<string, PineGuardContextUx> | null
): GuardContextMeta {
  const raw = context ? uxCatalog?.[context] : undefined;
  const fromApi =
    raw != null && typeof raw === "object" ? (raw as PineGuardContextUx) : undefined;
  if (fromApi?.label) {
    return {
      label: fromApi.label,
      tooltip: fromApi.tooltip ?? fromApi.description ?? fromApi.label,
    };
  }
  if (context && GUARD_CONTEXT_META[context]) return GUARD_CONTEXT_META[context];
  return {
    label: context ? String(context) : "—",
    tooltip: "Guard context from parse",
  };
}

export function normalizeParseIssue(
  raw: PineParseIssue | PineParseWarning,
  catalog?: Record<string, PineWarningCatalogEntry> | null
): PineParseIssue {
  const entry = catalog?.[raw.code];
  return {
    code: raw.code,
    severity: raw.severity ?? entry?.severity ?? "warning",
    title: raw.title ?? entry?.title ?? raw.code,
    message: raw.message || entry?.message || raw.code,
    expectation: raw.expectation ?? entry?.expectation,
    line: raw.line,
    line_end: raw.line_end,
    blocks: raw.blocks ?? entry?.blocks,
    guard_context: "guard_context" in raw ? (raw as PineParseIssue).guard_context : undefined,
    source: "source" in raw ? (raw as PineParseIssue).source : undefined,
    call_kind: "call_kind" in raw ? (raw as PineParseIssue).call_kind : undefined,
  };
}

export function getEffectiveParseIssues(
  saved?: ReportStrategyPineFields | null,
  parsed?: ReportStrategyPineParseResult | null,
  catalog?: Record<string, PineWarningCatalogEntry> | null
): PineParseIssue[] {
  const raw =
    parsed?.parse_issues ??
    parsed?.pine_parse_warnings ??
    saved?.pine_parse_warnings ??
    [];
  return raw.map((item) => normalizeParseIssue(item, catalog));
}

export function parseStatusAlertStatus(
  status?: PineParseStatus | string | null
): "success" | "warning" | "error" | "info" {
  switch (status) {
    case "full":
      return "success";
    case "partial":
      return "warning";
    case "failed":
      return "error";
    default:
      return "info";
  }
}

export type PineUxCopy = string | { label?: string; description?: string };

function isParseStatus(v: string): v is PineParseStatus {
  return v === "full" || v === "partial" || v === "failed";
}

function isInjectReadiness(v: string): v is InjectReadiness {
  return v === "ready" || v === "needs_review" || v === "unsupported" || v === "not_applicable";
}

function isExitHookReadiness(v: string): v is ExitHookReadiness {
  return v === "ready" || v === "partial" || v === "unsupported" || v === "not_applicable";
}

function mergeApiEnumUx(raw: PineUxCopy | undefined, fallback: PineEnumUx): PineEnumUx {
  if (raw == null) return fallback;
  if (typeof raw === "string") {
    const t = raw.trim();
    return t ? { ...fallback, description: t } : fallback;
  }
  const title =
    typeof raw.label === "string" && raw.label.trim() ? raw.label.trim() : fallback.title;
  const description =
    typeof raw.description === "string" && raw.description.trim()
      ? raw.description.trim()
      : fallback.description;
  const shortFromTitle = title.includes(":") ? title.split(":")[0].trim() : title;
  const shortLabel =
    shortFromTitle.length > 0 && shortFromTitle.length <= 40 ? shortFromTitle : fallback.shortLabel;
  return {
    shortLabel: humanizePineUserCopy(shortLabel),
    title: humanizePineUserCopy(title),
    description: humanizePineUserCopy(description),
  };
}

export function getParseStatusDisplay(
  status?: PineParseStatus | string | null,
  apiMap?: Record<string, PineUxCopy> | null
): PineEnumUx | null {
  if (!status || typeof status !== "string" || !isParseStatus(status)) return null;
  return mergeApiEnumUx(apiMap?.[status], PARSE_STATUS_UX_CANONICAL[status]);
}

export function getInjectReadinessDisplay(
  readiness?: InjectReadiness | string | null,
  apiMap?: Record<string, PineUxCopy> | null
): PineEnumUx | null {
  if (!readiness || typeof readiness !== "string" || !isInjectReadiness(readiness)) return null;
  return mergeApiEnumUx(apiMap?.[readiness], INJECT_READINESS_UX_CANONICAL[readiness]);
}

export function getExitHookReadinessDisplay(
  readiness?: ExitHookReadiness | string | null,
  apiMap?: Record<string, PineUxCopy> | null
): PineEnumUx | null {
  if (!readiness || typeof readiness !== "string" || !isExitHookReadiness(readiness)) return null;
  return mergeApiEnumUx(apiMap?.[readiness], EXIT_HOOK_READINESS_UX_CANONICAL[readiness]);
}

/** Hide inject chip when merge-into-strategy does not apply (e.g. indicator scripts). */
export function shouldShowInjectReadinessBadge(
  readiness?: InjectReadiness | string | null
): boolean {
  return Boolean(readiness && readiness !== "not_applicable");
}

export function getPineScriptKindDisplay(kind?: string | null): PineEnumUx | null {
  if (!kind || typeof kind !== "string") return null;
  const key = kind.trim().toLowerCase();
  const fallback = PINE_SCRIPT_KIND_UX_CANONICAL[key];
  if (!fallback) {
    return {
      shortLabel: humanizePineUserCopy(kind),
      title: humanizePineUserCopy(kind),
      description: humanizePineUserCopy(`Script kind: ${kind}.`),
    };
  }
  return {
    shortLabel: humanizePineUserCopy(fallback.shortLabel),
    title: humanizePineUserCopy(fallback.title),
    description: humanizePineUserCopy(fallback.description),
  };
}

/** Human labels for pine-requirements table column ids (BE keys stay unchanged). */
export function formatPineRequirementsColumnHeader(columnId: string): string {
  const map: Record<string, string> = {
    action: "Policy action",
    requirement: "Requirement",
    message: "Details",
    dashboard_sim: "In Tessera (simulator)",
    tradingview_compose: "On TradingView (composed Pinescript)",
  };
  return map[columnId] ?? columnId.replace(/_/g, " ");
}

/** User-facing Pinescript UX: plain language, no em dashes or Pine API identifiers. */
export function humanizePineUserCopy(text: string): string {
  let s = text;

  s = s.replace(/strategy\.entry\(\)/gi, "trade entry calls");
  s = s.replace(/strategy\.entry/gi, "trade entries");
  s = s.replace(/strategy\.exit\(\)/gi, "exit orders");
  s = s.replace(/strategy\.exit/gi, "exit orders");
  s = s.replace(/\bstrategy\(\)\s*script/gi, "strategy script");
  s = s.replace(/\btime_stop\b/g, "time-based exits");

  s = s.replace(/\s*—\s*/g, ", ");
  s = s.replace(/,\s*,/g, ", ");

  return s.trim();
}

export function coerceUxCopy(value: unknown): string | null {
  if (value == null) return null;
  let raw: string | null = null;
  if (typeof value === "string") {
    const t = value.trim();
    raw = t || null;
  } else if (typeof value === "object" && !Array.isArray(value)) {
    const o = value as { label?: unknown; description?: unknown };
    const desc = typeof o.description === "string" ? o.description.trim() : "";
    const label = typeof o.label === "string" ? o.label.trim() : "";
    if (desc && label) raw = `${label}: ${desc}`;
    else if (desc) raw = desc;
    else if (label) raw = label;
  }
  return raw ? humanizePineUserCopy(raw) : null;
}

export function parseStatusBannerMessage(
  status?: PineParseStatus | string | null,
  parseStatusUx?: Record<string, PineUxCopy> | null
): string | null {
  return getParseStatusDisplay(status, parseStatusUx)?.description ?? null;
}

export function injectReadinessMessage(
  readiness?: InjectReadiness | string | null,
  injectReadinessUx?: Record<string, PineUxCopy> | null
): string | null {
  return getInjectReadinessDisplay(readiness, injectReadinessUx)?.description ?? null;
}

export function entryMergeStatus(site: PineEntrySite): { label: string; colorScheme: string } {
  if (site.injectable) return { label: "Ready", colorScheme: "green" };
  const ctx = site.guard_context;
  if (ctx === "function_body" || ctx === "unsupported_enclosing" || ctx === "no_enclosing_if") {
    return { label: "Not supported", colorScheme: "red" };
  }
  return { label: "Needs review", colorScheme: "yellow" };
}

export function injectReadinessSummary(readiness?: InjectReadiness | string | null): {
  status: "success" | "warning" | "error" | "info";
  message: string;
} | null {
  const ux = getInjectReadinessDisplay(readiness);
  if (!ux) return null;
  switch (readiness) {
    case "ready":
      return { status: "success", message: ux.description };
    case "needs_review":
      return { status: "warning", message: ux.description };
    case "unsupported":
      return { status: "error", message: ux.description };
    case "not_applicable":
      return { status: "info", message: ux.description };
    default:
      return null;
  }
}

export function getPineIr(parse?: ReportStrategyPineParseResult | null): PineIr | undefined {
  const ir = parse?.pine_ir;
  return ir == null ? undefined : ir;
}

export function isIndicatorPineScript(
  saved?: ReportStrategyPineFields | null,
  parsed?: ReportStrategyPineParseResult | null
): boolean {
  const ir = getPineIr(parsed);
  const kind =
    (typeof ir?.script_kind === "string" ? ir.script_kind : null) ??
    parsed?.pine_script_kind ??
    saved?.pine_script_kind;
  if (kind === "indicator") return true;
  return getEffectiveInjectReadiness(saved, parsed) === "not_applicable";
}

export const APPEND_POLICY_TO_INDICATOR_USER_COPY =
  "Your indicator logic was not changed. Tessera added policy labels and variables at the end of the file. Your arrows still behave as before; Tessera labels show when Tessera policy would allow or block long/short.";

const COMPOSE_INFO_MESSAGES: Record<string, string> = {
  user_plots_unchanged: "Only appended at bottom; your plots/alerts unchanged.",
  policy_vars_at_end: "Policy variables (tessera_allowed_*) live in the appended block at end of file.",
};

export function normalizeComposeWarnings(
  warnings: Array<string | PineParseIssue | PineParseWarning> | undefined
): PineParseIssue[] {
  if (!warnings?.length) return [];
  return warnings.map((w) => {
    if (typeof w === "string") {
      return { code: "compose", message: w, severity: "info", title: "Compose" };
    }
    const code = w.code || "compose";
    return {
      code,
      severity: (w.severity as PineParseIssue["severity"]) ?? "info",
      title: w.title ?? code.replace(/_/g, " "),
      message: w.message || COMPOSE_INFO_MESSAGES[code] || code,
    };
  });
}

export function getEffectiveInjectReadiness(
  saved?: ReportStrategyPineFields | null,
  parsed?: ReportStrategyPineParseResult | null
): InjectReadiness | string | undefined {
  return (
    parsed?.inject_readiness ??
    parsed?.pine_ir?.inject_readiness ??
    saved?.inject_readiness
  );
}

export function getEffectiveExitHookReadiness(
  saved?: ReportStrategyPineFields | null,
  parsed?: ReportStrategyPineParseResult | null
): string | undefined {
  return (
    parsed?.exit_hook_readiness ??
    parsed?.pine_ir?.exit_hook_readiness ??
    saved?.exit_hook_readiness
  );
}

export function getEffectiveParseStatus(
  saved?: ReportStrategyPineFields | null,
  parsed?: ReportStrategyPineParseResult | null
): string | undefined {
  return parsed?.pine_parse_status ?? saved?.pine_parse_status;
}

export function getEffectiveParseWarnings(
  saved?: ReportStrategyPineFields | null,
  parsed?: ReportStrategyPineParseResult | null
): PineParseWarning[] {
  return parsed?.pine_parse_warnings ?? saved?.pine_parse_warnings ?? [];
}

export function issueSeverityAlertStatus(
  severity?: PineParseIssueSeverity | string
): "error" | "warning" | "info" {
  switch (severity) {
    case "error":
      return "error";
    case "info":
      return "info";
    default:
      return "warning";
  }
}

export function formatIssueBlocks(blocks?: string[]): string {
  if (!blocks?.length) return "Advisory";
  return blocks.join(", ");
}

const PARSE_ISSUE_DISPLAY_OVERRIDES: Record<
  string,
  { title?: string; message?: string }
> = {
  parse_error: {
    title: "Syntax error",
    message:
      "Fix the syntax on this line. Your script must pass Tessera's parse checks before you can save or compose.",
  },
  entry_not_in_if: {
    title: "Entry must sit inside an if guard",
    message: "Put this trade entry under an if block that includes your condition.",
  },
  entry_in_function: {
    title: "Entry inside a function",
    message: "Move the entry to chart scope or add // @tessera:inject long above the entry.",
  },
  missing_version_directive: {
    title: "Missing version header",
    message: "Add //@version=5 or //@version=6 as the first line of your script.",
  },
  indicator_only: {
    title: "Indicator script",
    message: "Merge into strategy is not available. Use Append policy or Separate indicator.",
  },
};

export function enrichParseIssueForDisplay(issue: PineParseIssue): PineParseIssue {
  const override = PARSE_ISSUE_DISPLAY_OVERRIDES[issue.code];
  const merged = override
    ? {
        ...issue,
        title: override.title ?? issue.title,
        message: override.message ?? issue.message,
      }
    : issue;
  return {
    ...merged,
    title: merged.title ? humanizePineUserCopy(merged.title) : merged.title,
    message: merged.message ? humanizePineUserCopy(merged.message) : merged.message,
  };
}

export type GroupedParseIssues = {
  blocksMerge: PineParseIssue[];
  review: PineParseIssue[];
  informational: PineParseIssue[];
};

export function groupParseIssuesByKind(issues: PineParseIssue[]): GroupedParseIssues {
  const enriched = issues.map(enrichParseIssueForDisplay);
  const blocksMerge: PineParseIssue[] = [];
  const review: PineParseIssue[] = [];
  const informational: PineParseIssue[] = [];
  for (const issue of enriched) {
    if (issue.blocks?.includes("merge") || issue.severity === "error") {
      blocksMerge.push(issue);
    } else if (issue.severity === "info") {
      informational.push(issue);
    } else {
      review.push(issue);
    }
  }
  return { blocksMerge, review, informational };
}

export type StrategyPineParseSummaryNarrative = {
  tone: "success" | "warning" | "error" | "info";
  title: string;
  description: string;
  /** Actionable fixes shown as a list inside the alert (create-strategy dialog). */
  fixItems?: string[];
};

function worstParseSummaryTone(
  a: StrategyPineParseSummaryNarrative["tone"],
  b: StrategyPineParseSummaryNarrative["tone"]
): StrategyPineParseSummaryNarrative["tone"] {
  const rank: Record<StrategyPineParseSummaryNarrative["tone"], number> = {
    error: 4,
    warning: 3,
    info: 2,
    success: 1,
  };
  return rank[b] > rank[a] ? b : a;
}

function issueNarrativeSentence(issue: PineParseIssue): string {
  const display = enrichParseIssueForDisplay(issue);
  const line =
    typeof display.line === "number"
      ? ` on line ${display.line}${
          display.line_end && display.line_end !== display.line ? `–${display.line_end}` : ""
        }`
      : "";
  const core = display.message?.trim() || display.title?.trim() || display.code;
  return `${core}${line}.`;
}

function allIssueSentences(issues: PineParseIssue[]): string[] {
  const { blocksMerge, review, informational } = groupParseIssuesByKind(issues);
  return [...blocksMerge, ...review, ...informational].map(issueNarrativeSentence);
}

/** Natural-language summary for the consolidated parse alert in create/edit strategy flows. */
export function buildStrategyPineParseSummaryNarrative(input: {
  fields: ReportStrategyPineFields;
  issues?: PineParseIssue[];
  parseStatusUx?: Record<string, PineUxCopy> | null;
  injectReadinessUx?: Record<string, PineUxCopy> | null;
  exitHookReadinessUx?: Record<string, PineUxCopy> | null;
}): StrategyPineParseSummaryNarrative | null {
  const { fields, issues = [], parseStatusUx, injectReadinessUx, exitHookReadinessUx } = input;
  if (!fields.pine_parse_status) return null;

  const parseUx = getParseStatusDisplay(fields.pine_parse_status, parseStatusUx);
  if (!parseUx) return null;

  let tone = parseStatusAlertStatus(fields.pine_parse_status);
  const injectUx = shouldShowInjectReadinessBadge(fields.inject_readiness)
    ? getInjectReadinessDisplay(fields.inject_readiness, injectReadinessUx)
    : null;
  const exitUx = fields.exit_hook_readiness
    ? getExitHookReadinessDisplay(fields.exit_hook_readiness, exitHookReadinessUx)
    : null;

  if (injectUx) {
    const injectTone =
      fields.inject_readiness === "unsupported"
        ? "error"
        : fields.inject_readiness === "needs_review"
          ? "warning"
          : fields.inject_readiness === "ready"
            ? "success"
            : "info";
    tone = worstParseSummaryTone(tone, injectTone);
  }
  if (fields.exit_hook_readiness === "unsupported") {
    tone = worstParseSummaryTone(tone, "warning");
  } else if (fields.exit_hook_readiness === "partial") {
    tone = worstParseSummaryTone(tone, "warning");
  }

  const { blocksMerge } = groupParseIssuesByKind(issues);
  const issueSentences = allIssueSentences(issues);
  const status = fields.pine_parse_status;

  if (status === "full" && tone === "success" && blocksMerge.length === 0) {
    const parts = [
      "Your Pinescript parsed successfully. Tessera can include it when you generate reports.",
    ];
    if (fields.inject_readiness === "ready") {
      parts.push(
        "Your entry guards look merge-friendly, so you can compose Tessera policy directly into this strategy."
      );
    }
    if (fields.exit_hook_readiness === "ready") {
      parts.push(
        "Named stop and limit variables were detected, so exit management can be hooked during compose."
      );
    }
    return {
      tone: "success",
      title: "Your script passed",
      description: parts.join(" "),
    };
  }

  if (status === "failed") {
    const fixItems = [...issueSentences];
    if (fixItems.length === 0 && parseUx.description.trim()) {
      fixItems.push(parseUx.description);
    }
    return {
      tone: "error",
      title: "Script did not pass",
      description:
        "This script did not pass Tessera's parse checks. Tessera cannot use your Pinescript in reports or merge policy into it until you fix the following.",
      fixItems: fixItems.length > 0 ? fixItems : undefined,
    };
  }

  const fixItems: string[] = [...issueSentences];
  const introParts: string[] = [];

  if (status === "partial") {
    introParts.push(
      "Your script did not fully pass Tessera's parse checks. Merge and compose are limited until you address the following."
    );
  } else if (status === "full") {
    introParts.push(
      "Your script parsed, but Tessera still needs the following addressed before merge."
    );
  }

  if (blocksMerge.length > 0 && issueSentences.length === 0) {
    fixItems.push("Resolve the structural issues that block merge and compose.");
  }

  if (fields.inject_readiness === "unsupported" && injectUx?.description?.trim()) {
    fixItems.push(injectUx.description);
  } else if (fields.inject_readiness === "needs_review" && injectUx?.description?.trim()) {
    fixItems.push(injectUx.description);
  }

  if (
    (fields.exit_hook_readiness === "unsupported" || fields.exit_hook_readiness === "partial") &&
    exitUx?.description?.trim()
  ) {
    fixItems.push(exitUx.description);
  }

  if (introParts.length === 0 && fixItems.length === 0 && status === "partial" && parseUx.description.trim()) {
    introParts.push(parseUx.description);
  }

  let title = parseUx.title;
  if (tone === "error") {
    title = "Script did not pass";
  } else if (blocksMerge.length > 0) {
    title = "Script needs fixes before merge";
  } else if (status === "partial") {
    title = "Script parsed with warnings";
  } else if (fields.inject_readiness === "needs_review") {
    title = "Script parsed but merge needs review";
  }

  return {
    tone,
    title,
    description: introParts.join(" ") || "Review the following before you save or compose.",
    fixItems: fixItems.length > 0 ? fixItems : undefined,
  };
}

export function groupWarningsByCode(warnings: PineParseWarning[]): Map<string, PineParseWarning[]> {
  const map = new Map<string, PineParseWarning[]>();
  for (const w of warnings) {
    const code = w.code || "unknown";
    const list = map.get(code) ?? [];
    list.push(w);
    map.set(code, list);
  }
  return map;
}

/** Pine save/create allowed when non-empty script matches last successful parse-preview. */
export function canSavePineAfterParse(script: string, parsedScriptSnapshot: string | null): boolean {
  const trimmed = script.trim();
  if (!trimmed) return false;
  return Boolean(parsedScriptSnapshot && parsedScriptSnapshot.trim() === trimmed);
}

export function scrollTextareaToLine(textarea: HTMLTextAreaElement, lineNumber: number): void {
  const line = Math.max(1, Math.floor(lineNumber));
  const lines = textarea.value.split("\n");
  let offset = 0;
  for (let i = 0; i < line - 1 && i < lines.length; i++) {
    offset += lines[i].length + 1;
  }
  const lineText = lines[line - 1] ?? "";
  textarea.focus();
  textarea.setSelectionRange(offset, offset + lineText.length);
  const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10) || 18;
  textarea.scrollTop = Math.max(0, (line - 3) * lineHeight);
}
